import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ArrowRight, Loader2, CheckCircle2, Sparkles,
  Building2, Phone, Mail, Briefcase, Zap, Calendar, Users,
  Lock, Eye, EyeOff
} from "lucide-react";
import axios from "axios";

const SECTORS = [
  { value: "barbiere", label: "Barbiere / Parrucchiere" },
  { value: "parrucchiere_donna", label: "Parrucchiere Donna" },
  { value: "dentista", label: "Studio Dentistico" },
  { value: "estetica", label: "Centro Estetico" },
  { value: "fisioterapia", label: "Studio Fisioterapia" },
  { value: "veterinario", label: "Ambulatorio Veterinario" },
  { value: "personal_trainer", label: "Personal Trainer / Palestra" },
  { value: "psicologo", label: "Studio Psicologia" },
  { value: "medico", label: "Studio Medico" },
  { value: "consulente", label: "Consulente" },
  { value: "altro", label: "Altro" },
];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const prefillVariant = searchParams.get("prefill"); // e.g. "demo-odonto"
  const sectorParam = searchParams.get("sector");
  const planParam = searchParams.get("plan"); // dental_pro | benessere_pro | growth

  const [phase, setPhase] = useState("intro"); // intro | form | loading | done
  const [form, setForm] = useState({
    business_name: "",
    sector: sectorParam || "",
    whatsapp: "",
    email: "",
    password: "",
    confirm_password: "",
    prefill_variant: prefillVariant || null,
  });
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    axios.post(`${API}/analytics/track`, {
      event: "onboarding_view",
      data: { prefill: prefillVariant, sector: sectorParam, plan: planParam }
    }).catch(() => {});
    // If coming from a demo variant, skip intro
    if (prefillVariant === "demo-odonto" || prefillVariant === "demo-benessere-posturale" || prefillVariant === "demo-estetica" || prefillVariant === "demo-hair") {
      setPhase("form");
    }
    // eslint-disable-next-line
  }, []);

  const pwValid = form.password.length >= 8 && /\d/.test(form.password);
  const pwMatch = form.password === form.confirm_password;

  const isValid = form.business_name.trim().length >= 2
    && form.sector
    && form.whatsapp.trim().length >= 8
    && form.email.includes("@")
    && pwValid && pwMatch;

  const handleSubmit = async () => {
    if (!isValid) return;
    setError("");
    setPhase("loading");

    // Animate progress
    const steps = [
      { pct: 15, delay: 300 },
      { pct: 35, delay: 800 },
      { pct: 55, delay: 1500 },
      { pct: 75, delay: 2200 },
      { pct: 90, delay: 2800 },
    ];
    steps.forEach(s => setTimeout(() => setProgress(s.pct), s.delay));

    try {
      const res = await axios.post(`${API}/onboarding/start`, form, { withCredentials: true });
      if (res.data.success) {
        setProgress(100);
        setTimeout(async () => {
          setPhase("done");
          await checkAuth();
          setTimeout(() => navigate("/dashboard"), 3000);
        }, 500);
      }
    } catch (err) {
      setPhase("form");
      setError(err.response?.data?.detail || "Errore durante la registrazione");
    }
  };

  // ========== INTRO ==========
  if (phase === "intro") {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4" data-testid="onboarding-intro">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full bg-emerald-500/5 blur-[120px]" />
          <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-blue-500/5 blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-md w-full text-center space-y-8">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center mx-auto">
            <Sparkles className="w-8 h-8" />
          </div>
          <div className="space-y-3">
            <h1 className="text-2xl sm:text-3xl font-bold leading-tight">
              Perfetto, iniziamo a configurare<br />
              <span className="text-emerald-400">SmartBooker</span> per la tua attivit&agrave;
            </h1>
            <p className="text-slate-400 text-sm">
              In meno di 60 secondi avrai il tuo sistema pronto. Nessuna password, nessuna carta.
            </p>
          </div>
          <div className="flex items-center justify-center gap-6 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-emerald-500" /> 14 giorni gratis</span>
            <span className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-emerald-500" /> Setup automatico</span>
            <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-emerald-500" /> Zero frizione</span>
          </div>
          <button
            onClick={() => setPhase("form")}
            className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold text-base shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all"
            data-testid="onboarding-start-btn"
          >
            Iniziamo <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // ========== FORM ==========
  if (phase === "form") {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4" data-testid="onboarding-form">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full bg-emerald-500/5 blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-md w-full space-y-8">
          {prefillVariant === "demo-odonto" && (
            <div className="rounded-2xl border border-cyan-500/40 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 p-4 text-center" data-testid="onboarding-odonto-prefill-badge">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wider text-cyan-300 bg-cyan-500/10 px-2 py-0.5 rounded-full border border-cyan-500/20 mb-2">
                <Sparkles className="w-3 h-3" /> PRE-CONFIGURATO PER TE
              </div>
              <p className="text-sm text-white font-medium">Studio odontoiatrico pronto all'uso</p>
              <p className="text-xs text-slate-400 mt-1">Servizi, operatori, poltrone, recall e template WhatsApp gi&agrave; impostati.</p>
            </div>
          )}
          {prefillVariant === "demo-benessere-posturale" && (
            <div className="rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-sky-500/10 p-4 text-center" data-testid="onboarding-fisio-prefill-badge">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wider text-emerald-300 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20 mb-2">
                <Sparkles className="w-3 h-3" /> PRE-CONFIGURATO PER TE
              </div>
              <p className="text-sm text-white font-medium">Studio fisio / osteo pronto all'uso</p>
              <p className="text-xs text-slate-400 mt-1">Servizi, operatori, sale e recall post-ciclo gi&agrave; impostati.</p>
            </div>
          )}
          {prefillVariant === "demo-estetica" && (
            <div className="rounded-2xl border border-pink-500/40 bg-gradient-to-br from-pink-500/10 to-rose-500/10 p-4 text-center" data-testid="onboarding-estetica-prefill-badge">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wider text-pink-300 bg-pink-500/10 px-2 py-0.5 rounded-full border border-pink-500/20 mb-2">
                <Sparkles className="w-3 h-3" /> PRE-CONFIGURATO PER TE
              </div>
              <p className="text-sm text-white font-medium">Centro estetico pronto all'uso</p>
              <p className="text-xs text-slate-400 mt-1">Servizi, estetiste, cabine e recall clienti dormienti gi&agrave; impostati.</p>
            </div>
          )}
          {prefillVariant === "demo-hair" && (
            <div className="rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-orange-500/10 p-4 text-center" data-testid="onboarding-hair-prefill-badge">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wider text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/20 mb-2">
                <Sparkles className="w-3 h-3" /> PRE-CONFIGURATO PER TE
              </div>
              <p className="text-sm text-white font-medium">Salone parrucchieri / barber pronto all'uso</p>
              <p className="text-xs text-slate-400 mt-1">Servizi, stylist, postazioni e recall clienti gi&agrave; impostati.</p>
            </div>
          )}
          {(planParam === "benessere_pro" || planParam === "dental_pro") && (
            <div className="rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-rose-500/10 p-4 text-center shadow-lg shadow-amber-500/10" data-testid="onboarding-pro-trial-banner">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-black tracking-wider text-amber-300 bg-amber-500/20 px-2.5 py-0.5 rounded-full border border-amber-400/40 mb-2">
                ★ TRIAL PRO ATTIVATO
              </div>
              <p className="text-sm text-white font-bold">14 giorni gratis di Smartbooker <span className="text-amber-300">PRO</span> (€179/mese)</p>
              <p className="text-xs text-slate-300 mt-1">Tutte le funzioni sbloccate: pacchetti, recall, pagamenti, report. Nessuna carta richiesta.</p>
            </div>
          )}
          <div className="text-center space-y-2">
            <h2 className="text-xl sm:text-2xl font-bold">Parlaci della tua attivit&agrave;</h2>
            <p className="text-slate-400 text-sm">4 informazioni e sei dentro</p>
          </div>

          <div className="space-y-4">
            {/* Business name */}
            <div className="space-y-1.5">
              <label className="text-xs text-slate-400 font-medium flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" /> Nome attivit&agrave;
              </label>
              <Input
                value={form.business_name}
                onChange={(e) => setForm(p => ({ ...p, business_name: e.target.value }))}
                placeholder="Es. Studio Rossi, BarberShop Milano..."
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-600 rounded-xl h-11 focus:border-emerald-500/50"
                data-testid="onboarding-name"
              />
            </div>

            {/* Sector */}
            <div className="space-y-1.5">
              <label className="text-xs text-slate-400 font-medium flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5" /> Settore
              </label>
              <Select value={form.sector} onValueChange={(v) => setForm(p => ({ ...p, sector: v }))}>
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white rounded-xl h-11" data-testid="onboarding-sector">
                  <SelectValue placeholder="Seleziona il tuo settore" />
                </SelectTrigger>
                <SelectContent>
                  {SECTORS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* WhatsApp */}
            <div className="space-y-1.5">
              <label className="text-xs text-slate-400 font-medium flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5" /> Numero WhatsApp
              </label>
              <Input
                value={form.whatsapp}
                onChange={(e) => setForm(p => ({ ...p, whatsapp: e.target.value }))}
                placeholder="Es. +39 377 087 3260"
                type="tel"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-600 rounded-xl h-11 focus:border-emerald-500/50"
                data-testid="onboarding-whatsapp"
              />
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-xs text-slate-400 font-medium flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> Email
              </label>
              <Input
                value={form.email}
                onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                placeholder="La tua email per accedere"
                type="email"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-600 rounded-xl h-11 focus:border-emerald-500/50"
                data-testid="onboarding-email"
              />
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="text-xs text-slate-400 font-medium flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" /> Password
              </label>
              <div className="relative">
                <Input
                  value={form.password}
                  onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="Minimo 8 caratteri, 1 numero"
                  type={showPw ? "text" : "password"}
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-600 rounded-xl h-11 focus:border-emerald-500/50 pr-10"
                  data-testid="onboarding-password"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {form.password && !pwValid && (
                <p className="text-[11px] text-red-400">Minimo 8 caratteri e almeno 1 numero</p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-1.5">
              <label className="text-xs text-slate-400 font-medium flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" /> Conferma Password
              </label>
              <Input
                value={form.confirm_password}
                onChange={(e) => setForm(p => ({ ...p, confirm_password: e.target.value }))}
                placeholder="Ripeti la password"
                type={showPw ? "text" : "password"}
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-600 rounded-xl h-11 focus:border-emerald-500/50"
                data-testid="onboarding-confirm-password"
              />
              {form.confirm_password && !pwMatch && (
                <p className="text-[11px] text-red-400">Le password non coincidono</p>
              )}
            </div>

            {error && (
              <p className="text-red-400 text-sm text-center" data-testid="onboarding-error">{error}</p>
            )}
          </div>

          <button
            onClick={handleSubmit}
            disabled={!isValid}
            className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold text-base shadow-lg shadow-emerald-500/20 hover:shadow-xl disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            data-testid="onboarding-submit"
          >
            Attiva prova gratuita <ArrowRight className="w-5 h-5" />
          </button>

          <p className="text-center text-[11px] text-slate-600">
            Nessuna carta di credito richiesta. 14 giorni gratis.
          </p>
        </div>
      </div>
    );
  }

  // ========== LOADING ==========
  if (phase === "loading") {
    const steps = [
      { label: "Creo il tuo account...", threshold: 0 },
      { label: "Attivo la prova gratuita...", threshold: 20 },
      { label: "Genero i servizi...", threshold: 40 },
      { label: "Configuro il calendario...", threshold: 60 },
      { label: "Quasi pronto...", threshold: 85 },
    ];
    const currentLabel = [...steps].reverse().find(s => progress >= s.threshold)?.label || steps[0].label;

    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4" data-testid="onboarding-loading">
        <div className="max-w-sm w-full text-center space-y-8">
          <Loader2 className="w-10 h-10 text-emerald-400 animate-spin mx-auto" />
          <div className="space-y-3">
            <h2 className="text-xl font-bold">Stiamo preparando tutto</h2>
            <p className="text-slate-400 text-sm">{currentLabel}</p>
          </div>
          <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  // ========== DONE ==========
  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4" data-testid="onboarding-done">
      <div className="max-w-sm w-full text-center space-y-8">
        <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto animate-in zoom-in-50 duration-500">
          <CheckCircle2 className="w-10 h-10 text-emerald-400" />
        </div>
        <div className="space-y-3">
          <h2 className="text-2xl font-bold">Tutto pronto!</h2>
          <p className="text-slate-400 text-sm">
            La tua attivit&agrave; <strong className="text-white">{form.business_name}</strong> &egrave; configurata.
          </p>
        </div>

        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-left space-y-2">
          <p className="text-xs font-semibold text-emerald-400 flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" /> Ti abbiamo inviato un link
          </p>
          <p className="text-xs text-slate-300">
            Controlla la tua email <strong>{form.email}</strong> per accedere quando vuoi, anche da altri dispositivi.
          </p>
        </div>

        <p className="text-xs text-slate-500">Redirect automatico alla dashboard in 3 secondi...</p>
      </div>
    </div>
  );
};

export default OnboardingPage;
